import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useAuthStore } from '../../stores/authStore';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import CardNB from '../../components/ui-nb/CardNB';
import ButtonNB from '../../components/ui-nb/ButtonNB';
import IconNB from '../../components/ui-nb/IconNB';
import InputNB from '../../components/ui-nb/InputNB';
import SelectNB from '../../components/ui-nb/SelectNB';
import DatePickerNB from '../../components/ui-nb/DatePickerNB';
import PhotoSessionPanel, { type PhonePhoto } from '../../components/orders/PhotoSessionPanel';
import CreateSparePartModal, { type SparePartData } from '../../components/spare-parts/CreateSparePartModal';
import { useToast } from '../../components/ToastNB';
import { getFunctionErrorMessage } from '../../lib/errors';
import { error as logError } from '../../lib/logger';
import { useTranslation } from '../../i18n';
import { exportOrderPDF, printOrderReceipt, buildOrderReceiptData } from '../../lib/order-pdf';
import { generateTrackQR } from '../../lib/qr-code';

interface FormData {
  clientName: string;
  clientPhone: string;
  clientEmail: string;
  equipmentType: string;
  equipmentBrand: string;
  equipmentModel: string;
  failureDescription: string;
  collectionDate: string;
  estimatedDeliveryDate: string;
  requiresPart: 'yes' | 'no';
  requiresPartName: string;
  estimatedPrice: string;
  imeiSerial: string;
  accessories: string;
  passwordPattern: string;
  priority: 'normal' | 'urgent';
  internalNotes: string;
}

interface PhotoFile {
  file: File;
  preview: string;
}

export default function ReceiveEquipment() {
  const navigate = useNavigate();
  const { lang } = useParams();
  const user = useAuthStore(s => s.user);
  const organizationId = user?.organizationId;
  const userId = user?.id;

  const [photos, setPhotos] = useState<PhotoFile[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [equipmentTypes, setEquipmentTypes] = useState<{ value: string; label: string }[]>([]);
  const [loadingTypes, setLoadingTypes] = useState(true);
  const [branches, setBranches] = useState<{ id: string; name: string }[]>([]);
  const [selectedBranchId, setSelectedBranchId] = useState<string>('');
  const [isSuccess, setIsSuccess] = useState(false);
  const [redirectCountdown, setRedirectCountdown] = useState(10);
  const successRef = useRef(false);
  const [phonePhotos, setPhonePhotos] = useState<PhonePhoto[]>([]);
  const [sessionToken, setSessionToken] = useState<string | null>(null);
  const [isPhotoPanelOpen, setIsPhotoPanelOpen] = useState(false);
  const [createdOrder, setCreatedOrder] = useState<Record<string, unknown> | null>(null);
  const [showSparePartModal, setShowSparePartModal] = useState(false);
  const [sparePartData, setSparePartData] = useState<SparePartData | null>(null);
  const { addToast } = useToast();
  const { t, language } = useTranslation();

  // Hide the "scan with phone" QR flow on mobile — the user IS the phone,
  // so the only viable path is the native file picker below.
  const isMobile = useMediaQuery('(max-width: 767px)');

  // Auth protection redirect
  useEffect(() => {
    if (!user) navigate('/login', { replace: true });
  }, [user, navigate]);

  // Countdown redirect after success
  useEffect(() => {
    if (!isSuccess) {
      successRef.current = false;
      return;
    }
    successRef.current = true;
    setRedirectCountdown(10);
    const interval = setInterval(() => {
      setRedirectCountdown(prev => {
        if (prev <= 1) {
          clearInterval(interval);
          if (successRef.current) {
            navigate(`/${lang || 'en'}/admin/orders`);
          }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => {
      clearInterval(interval);
      successRef.current = false;
    };
  }, [isSuccess, navigate, lang]);

  // Fetch equipment types from DB
  useEffect(() => {
    if (!organizationId) return;

    async function fetchEquipmentTypes() {
      try {
        const { data, error: fetchError } = await supabase
          .from('equipment_types')
          .select('name')
          .eq('organization_id', organizationId)
          .order('name');

        if (fetchError) throw fetchError;

        const types = (data ?? []).map((t: { name: string }) => ({
          value: t.name.toLowerCase().replace(/\s+/g, '_'),
          label: t.name,
        }));

        setEquipmentTypes(types);
      } catch (err) {
        logError('Error fetching equipment types', { error: err });
      } finally {
        setLoadingTypes(false);
      }
    }

    fetchEquipmentTypes();
  }, [organizationId]);

  // Fetch branches for branch selector
  useEffect(() => {
    if (!organizationId) return;
    async function fetchBranches() {
      const { data } = await supabase
        .from('branches')
        .select('id, name')
        .eq('organization_id', organizationId)
        .order('name');
      if (data && data.length > 0) {
        setBranches(data);
        if (data.length === 1) setSelectedBranchId(data[0].id);
      }
    }
    fetchBranches();
  }, [organizationId]);

  const [formData, setFormData] = useState<FormData>({
    clientName: '',
    clientPhone: '',
    clientEmail: '',
    equipmentType: '',
    equipmentBrand: '',
    equipmentModel: '',
    failureDescription: '',
    collectionDate: new Date().toISOString().split('T')[0],
    estimatedDeliveryDate: new Date(Date.now() + 86400000).toISOString().split('T')[0],
    requiresPart: 'no',
    requiresPartName: '',
    estimatedPrice: '',
    imeiSerial: '',
    accessories: '',
    passwordPattern: '',
    priority: 'normal',
    internalNotes: '',
  });

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    const newPhotos: PhotoFile[] = [];
    const remainingSlots = 5 - photos.length - phonePhotos.length;
    for (let i = 0; i < Math.min(files.length, remainingSlots); i++) {
      newPhotos.push({
        file: files[i],
        preview: URL.createObjectURL(files[i]),
      });
    }
    setPhotos(prev => [...prev, ...newPhotos].slice(0, 5));
  };

  const removePhoto = (index: number) => {
    setPhotos(prev => {
      const newPhotos = [...prev];
      URL.revokeObjectURL(newPhotos[index].preview);
      newPhotos.splice(index, 1);
      return newPhotos;
    });
  };

  // Compress image using Canvas API before upload
  async function compressImage(file: File, maxWidth = 1024, quality = 0.4): Promise<Blob> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        let { width, height } = img;
        if (width > maxWidth) {
          height = (height * maxWidth) / width;
          width = maxWidth;
        }
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d')!;
        ctx.drawImage(img, 0, 0, width, height);
        canvas.toBlob(blob => {
          if (blob) resolve(blob);
          else reject(new Error('Compression failed'));
        }, 'image/jpeg', quality);
      };
      img.onerror = () => reject(new Error('Failed to load image'));
      img.src = URL.createObjectURL(file);
    });
  }

  const composeFailureDescription = (): string => {
    let desc = formData.failureDescription;
    if (formData.imeiSerial.trim()) desc += `\n\nIMEI/Serial: ${formData.imeiSerial.trim()}`;
    if (formData.accessories.trim()) desc += `\nAccessories: ${formData.accessories.trim()}`;
    if (formData.passwordPattern.trim()) desc += `\nPassword/Pattern: ${formData.passwordPattern.trim()}`;
    if (formData.requiresPart === 'yes' && sparePartData?.description) desc += `\nPart required: ${sparePartData.description}`;
    if (formData.estimatedPrice.trim()) desc += `\nEstimated price: $${formData.estimatedPrice.trim()}`;
    if (formData.internalNotes.trim()) desc += `\n\nInternal notes: ${formData.internalNotes.trim()}`;
    return desc;
  };

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (!selectedBranchId) errors.branch = 'Branch is required';
    if (!formData.clientName.trim()) errors.clientName = 'Customer name is required';
    if (!formData.clientPhone.trim() && !formData.clientEmail.trim()) {
      errors.clientPhone = 'Phone or email is required';
    }
    if (formData.clientPhone.trim() && !/^[\d\s\-\+\(\)]{7,20}$/.test(formData.clientPhone.trim())) {
      errors.clientPhone = 'Invalid phone number';
    }
    if (formData.clientEmail.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.clientEmail.trim())) {
      errors.clientEmail = 'Invalid email address';
    }
    if (!formData.equipmentType) errors.equipmentType = 'Equipment type is required';
    if (!formData.failureDescription.trim()) errors.failureDescription = 'Problem description is required';
    if (formData.collectionDate && formData.estimatedDeliveryDate && formData.collectionDate > formData.estimatedDeliveryDate) {
      errors.estimatedDeliveryDate = 'Cannot be before collection date';
    }
    if (formData.estimatedPrice && (isNaN(Number(formData.estimatedPrice)) || Number(formData.estimatedPrice) < 0)) {
      errors.estimatedPrice = 'Price must be a positive number';
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFieldErrors({});

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const { data, error: funcError } = await supabase.functions.invoke('create-order', {
        body: {
          branch_id: selectedBranchId,
          customer_name: formData.clientName,
          customer_contact: [formData.clientPhone, formData.clientEmail].filter(Boolean).join(' | ') || null,
          device: formData.equipmentType,
          device_brand: formData.equipmentBrand || null,
          device_model: formData.equipmentModel || null,
          failure_description: composeFailureDescription(),
          priority: formData.priority,
          estimated_date: formData.estimatedDeliveryDate || null,
          assigned_to: userId,
        },
      });

      if (funcError) throw funcError;

      const order = data?.order;
      if (!order?.id) throw new Error('Order created but no ID returned');

      // Move phone photos from temp to permanent storage
      if (sessionToken && phonePhotos.length > 0) {
        try {
          const { error: moveError } = await supabase.functions.invoke('move-temp-photos', {
            body: { sessionToken, orderId: order.id },
          });
          if (moveError) {
            logError('Failed to move phone photos', { error: moveError });
          }
        } catch (err) {
          logError('move-temp-photos exception', { error: err });
        }
      }

      // Upload photos after order creation
      if (photos.length > 0) {
        let failedCount = 0;
        for (let i = 0; i < photos.length; i++) {
          try {
            const filename = `${Date.now()}-${i}.jpg`;
            const storagePath = `${order.id}/${filename}`;

            // Compress image before upload
            const compressedBlob = await compressImage(photos[i].file);

            const { error: uploadError } = await supabase.storage
              .from('order-photos')
              .upload(storagePath, compressedBlob, {
                contentType: 'image/jpeg',
                upsert: false,
              });

            if (uploadError) {
              logError(`Photo ${i} upload error`, { error: uploadError });
              failedCount++;
              continue;
            }

            const { data: urlData } = supabase.storage.from('order-photos').getPublicUrl(storagePath);

            await supabase.from('order_photo').insert({
              order_id: order.id,
              url: urlData.publicUrl,
              type: 'evidence',
            });
          } catch (err) {
            logError(`Photo ${i} upload exception`, { error: err });
            failedCount++;
          }
        }

        if (failedCount === photos.length) {
          addToast('Order created but all photos failed to upload.', 'error');
          setIsSubmitting(false);
          return;
        }
      }

      setCreatedOrder(order);

      // If user saved a spare part via the deferred modal, insert it now
      if (sparePartData && order.id) {
        try {
          const { error: insertError } = await supabase
            .from('spare_parts')
            .insert({
              order_id: order.id as string,
              description: sparePartData.description,
              status: 'sin_pedir',
              tracking_number: sparePartData.trackingNumber || null,
            });
            if (insertError) {
            logError('Failed to create spare part', { error: insertError });
          }
        } catch (err) {
          logError('Spare part insert exception', { error: err });
        }
      }

      setIsSuccess(true);
    } catch (err) {
      logError('Error creating order', { error: err });
      addToast(
        getFunctionErrorMessage(err, 'Error registering equipment. Please try again.'),
        'error'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const clearFieldError = (field: string) => {
    setFieldErrors(prev => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const handleReset = () => {
    setFormData({
      clientName: '',
      clientPhone: '',
      clientEmail: '',
      equipmentType: '',
      equipmentBrand: '',
      equipmentModel: '',
      failureDescription: '',
      collectionDate: new Date().toISOString().split('T')[0],
      estimatedDeliveryDate: '',
      requiresPart: 'no',
      requiresPartName: '',
      estimatedPrice: '',
      imeiSerial: '',
      accessories: '',
      passwordPattern: '',
      priority: 'normal',
      internalNotes: '',
    });
    photos.forEach(p => URL.revokeObjectURL(p.preview));
    setPhotos([]);
    setPhonePhotos([]);
    setCreatedOrder(null);
    setShowSparePartModal(false);
    setSparePartData(null);
    setIsSuccess(false);
    successRef.current = false;
    setRedirectCountdown(0);
    setError(null);
    setFieldErrors({});
    setIsSubmitting(false);
  };

  const handlePrintTicket = useCallback(async () => {
    if (!createdOrder) return;
    try {
      const receipt = buildOrderReceiptData(createdOrder);
      const qr = await generateTrackQR(receipt.release_code, language);
      printOrderReceipt(receipt, language, qr, t);
    } catch (err) {
      logError('Print failed', { error: err });
      addToast('Failed to print ticket', 'error');
    }
  }, [createdOrder, language, t, addToast]);

  const handleDownloadTicket = useCallback(async () => {
    if (!createdOrder) return;
    try {
      const receipt = buildOrderReceiptData(createdOrder);
      const qr = await generateTrackQR(receipt.release_code, language);
      await exportOrderPDF(receipt, language, qr, t);
      addToast('Ticket downloaded', 'success');
    } catch (err) {
      logError('Download failed', { error: err });
      addToast('Failed to download ticket', 'error');
    }
  }, [createdOrder, language, t, addToast]);

  if (isSuccess) {
    return (
      <div className="max-w-2xl mx-auto bg-nb-cream">
        <CardNB className="p-8 text-center border-3 border-nb-black">
          <div className="w-16 h-16 rounded-[2px] flex items-center justify-center mx-auto mb-4 bg-nb-black">
            <IconNB name="check" size={32} className="text-white" />
          </div>
          <h2
            className="text-2xl font-bold text-black font-nb mb-2">
            Equipment registered!
          </h2>
          <p className="mb-6 text-nb-text-muted">The equipment information has been saved successfully.</p>
          <div className="flex flex-col items-center gap-3 max-w-xs mx-auto">
            <div className="relative w-full">
              <ButtonNB
                variant="primary"
                size="md"
                className="w-full"
                onClick={() => navigate(`/${lang || 'en'}/admin/orders`)}>
                Go to my orders
              </ButtonNB>
              {redirectCountdown > 0 && (
                <div className="absolute bottom-0 left-0 right-0 h-1 bg-nb-black/20 rounded-b-[2px] overflow-hidden">
                  <div
                    className="h-full bg-nb-yellow transition-[width] duration-1000 ease-linear"
                    style={{ width: `${(redirectCountdown / 10) * 100}%` }}
                  />
                </div>
              )}
            </div>
            <div className="flex flex-col gap-2 w-full">
              <ButtonNB
                variant="secondary"
                size="md"
                className="w-full"
                onClick={handleReset}>
                Register another equipment
              </ButtonNB>
              <div className="grid grid-cols-2 gap-2 w-full">
                <ButtonNB
                  variant="secondary"
                  size="md"
                  className="w-full"
                  onClick={handlePrintTicket}>
                  <IconNB name="printer" size={16} />
                  Print ticket
                </ButtonNB>
                <ButtonNB
                  variant="secondary"
                  size="md"
                  className="w-full"
                  onClick={handleDownloadTicket}>
                  <IconNB name="download" size={16} />
                  Download ticket
                </ButtonNB>
              </div>
            </div>
          </div>
        </CardNB>
      </div>
    );
  }

  // Auth guard — show redirecting message
  if (!user) {
    return (
      <div className="max-w-2xl mx-auto bg-nb-cream py-16">
        <CardNB className="p-8 text-center">
          <p className="text-nb-text-muted">Redirecting to login...</p>
        </CardNB>
      </div>
    );
  }

  // Guard: require equipment types configured before showing form
  if (!loadingTypes && equipmentTypes.length === 0) {
    return (
      <div className="max-w-2xl mx-auto py-16 bg-nb-cream">
        <CardNB className="p-8 text-center">
          <div className="w-16 h-16 rounded-[2px] flex items-center justify-center mx-auto mb-4 bg-nb-cream border-2 border-black">
            <IconNB name="equipment" size={32} />
          </div>
          <h2 className="text-xl font-bold text-black mb-2">
            No equipment types configured
          </h2>
          <p className="mb-6 text-nb-text-muted">
            Equipment types must be configured before receiving orders.
            Visit the admin dashboard to set them up.
          </p>
          <ButtonNB
            variant="primary"
            size="md"
            onClick={() => navigate(`/${lang || 'en'}/admin/equipment-types`)}>
            <IconNB name="settings" size={16} />
            Configure Equipment Types
          </ButtonNB>
        </CardNB>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto bg-nb-cream">
      <div className="mb-6">
        <h1
          className="text-2xl font-bold text-black font-nb mb-1">
          Receive Equipment
        </h1>
        <p className="text-sm text-nb-text-muted">Register the received equipment information</p>
      </div>

      <CardNB className="overflow-hidden">
        <form onSubmit={handleSubmit}>
          <div className="p-6 md:p-8 space-y-8">

            {/* Branch selector */}
            <section>
              <SelectNB
                label="Branch *"
                value={selectedBranchId}
                onChange={e => { setSelectedBranchId(e.target.value); setFieldErrors(prev => ({ ...prev, branch: '' })); }}
                error={fieldErrors.branch}>
                {branches.length === 0 && <option value="">Loading branches...</option>}
                {branches.length > 0 && <option value="">Select branch</option>}
                {branches.map(b => (
                  <option key={b.id} value={b.id}>{b.name}</option>
                ))}
              </SelectNB>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-black mb-4 flex items-center gap-2">
                <span className="w-8 h-8 rounded-[2px] flex items-center justify-center text-sm font-bold bg-nb-yellow text-black border-2 border-black">1</span>
                Customer information
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <InputNB
                    label="Client name *"
                    value={formData.clientName}
                    onChange={e => { setFormData(prev => ({ ...prev, clientName: e.target.value })); clearFieldError('clientName'); }}
                    placeholder="Full name"
                    error={fieldErrors.clientName}
                  />
                </div>
                <InputNB
                  label="Phone"
                  type="tel"
                  value={formData.clientPhone}
                  onChange={e => { setFormData(prev => ({ ...prev, clientPhone: e.target.value })); clearFieldError('clientPhone'); }}
                  placeholder="+1 555 0000"
                  error={fieldErrors.clientPhone}
                />
                <InputNB
                  label="Email"
                  type="email"
                  value={formData.clientEmail}
                  onChange={e => { setFormData(prev => ({ ...prev, clientEmail: e.target.value })); clearFieldError('clientEmail'); }}
                  placeholder="maria@example.com"
                  error={fieldErrors.clientEmail}
                />
              </div>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-black mb-4 flex items-center gap-2">
                <span className="w-8 h-8 rounded-[2px] flex items-center justify-center text-sm font-bold bg-nb-yellow text-black border-2 border-black">2</span>
                Equipment
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <SelectNB
                    label="Equipment type *"
                    value={formData.equipmentType}
                    onChange={e => { setFormData(prev => ({ ...prev, equipmentType: e.target.value })); clearFieldError('equipmentType'); }}
                    required
                    error={fieldErrors.equipmentType}>
                    <option value="">{
                      equipmentTypes.length === 0
                        ? 'No types configured'
                        : 'Select type'
                    }</option>
                    {equipmentTypes.map(type => (
                      <option key={type.value} value={type.value}>{type.label}</option>
                    ))}
                  </SelectNB>
                </div>
                <InputNB
                  label="Brand"
                  value={formData.equipmentBrand}
                  onChange={e => setFormData(prev => ({ ...prev, equipmentBrand: e.target.value }))}
                  placeholder="Apple, Samsung, etc."
                />
                <InputNB
                  label="Model"
                  value={formData.equipmentModel}
                  onChange={e => setFormData(prev => ({ ...prev, equipmentModel: e.target.value }))}
                  placeholder="iPhone 14 Pro"
                />
              </div>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-black mb-4 flex items-center gap-2">
                <span className="w-8 h-8 rounded-[2px] flex items-center justify-center text-sm font-bold bg-nb-yellow text-black border-2 border-black">3</span>
                Problem description
              </h2>
              <textarea
                required
                rows={4}
                value={formData.failureDescription}
                onChange={e => { setFormData(prev => ({ ...prev, failureDescription: e.target.value })); clearFieldError('failureDescription'); }}
                className={`w-full px-4 py-2.5 bg-white border-2 ${fieldErrors.failureDescription ? 'border-nb-red' : 'border-black'} text-black focus:outline-none focus:border-nb-yellow resize-none rounded-[2px]`}
                placeholder="Describe the problem reported by the client..."
              />
              {fieldErrors.failureDescription && (
                <span className="text-xs text-nb-red mt-1 block">{fieldErrors.failureDescription}</span>
              )}
            </section>

            <section>
              <h2 className="text-lg font-semibold text-black mb-4 flex items-center gap-2">
                <span className="w-8 h-8 rounded-[2px] flex items-center justify-center text-sm font-bold bg-nb-yellow text-black border-2 border-black">4</span>
                Photos
              </h2>
              {/* Phone photo capture (desktop only — mobile uses the file picker) */}
              {!isMobile && (
                <div style={{ marginBottom: '12px' }}>
                  <PhotoSessionPanel
                    onPhotosChange={setPhonePhotos}
                    onSessionCreated={setSessionToken}
                    maxPhotos={5}
                    currentCount={photos.length}
                    isOpen={isPhotoPanelOpen}
                    onClose={() => setIsPhotoPanelOpen(false)}
                    onToggle={() => setIsPhotoPanelOpen((prev) => !prev)}
                  />
                </div>
              )}
              <div
                className="border-2 border-dashed border-black p-6 text-center"
                onDragOver={e => { e.preventDefault(); e.currentTarget.style.borderColor = '#FFD600'; }}
                onDragLeave={e => { e.preventDefault(); e.currentTarget.style.borderColor = '#000'; }}
                onDrop={e => {
                  e.preventDefault();
                  e.currentTarget.style.borderColor = '#000';
                  const files = e.dataTransfer.files;
                  if (files && files.length > 0) {
                    const newPhotos: PhotoFile[] = [];
                    for (let i = 0; i < Math.min(files.length, 5 - photos.length - phonePhotos.length); i++) {
                      if (files[i].type.startsWith('image/')) {
                        newPhotos.push({
                          file: files[i],
                          preview: URL.createObjectURL(files[i]),
                        });
                      }
                    }
                    setPhotos(prev => [...prev, ...newPhotos].slice(0, 5));
                  }
                }}>
                <label className="cursor-pointer block">
                  <div className="flex flex-col items-center gap-2">
                    <IconNB name="equipment" size={40} />
                    <span className="text-sm text-nb-text-muted">Upload photos ({photos.length + phonePhotos.length}/5) — drag & drop or click</span>
                    <span className="text-xs text-nb-text-muted">PNG, JPG up to 10MB</span>
                  </div>
                  <input
                    type="file"
                    accept="image/*"
                    capture="environment"
                    multiple
                    onChange={handlePhotoChange}
                    className="hidden"
                    disabled={photos.length + phonePhotos.length >= 5}
                  />
                </label>
              </div>
              {(photos.length > 0 || phonePhotos.length > 0) && (
                <div className="grid grid-cols-3 md:grid-cols-5 gap-3 mt-4">
                  {/* Phone photos */}
                  {phonePhotos.map((photo) => (
                    <div key={`phone-${photo.index}`} className="relative group aspect-square">
                      <img
                        src={photo.url}
                        alt={`Phone photo ${photo.index + 1}`}
                        className="w-full h-full object-cover rounded-[2px] border border-black"
                      />
                      <span className="absolute top-1 left-1 w-5 h-5 rounded-[2px] flex items-center justify-center text-[10px] bg-nb-black text-white">
                        📱
                      </span>
                    </div>
                  ))}
                  {photos.map((photo, index) => (
                    <div key={index} className="relative group aspect-square">
                      <img
                        src={photo.preview}
                        alt={`Photo ${index + 1}`}
                        className="w-full h-full object-cover rounded-[2px] border border-black"
                      />
                      <button
                        type="button"
                        onClick={() => removePhoto(index)}
                        className="absolute -top-2 -right-2 w-6 h-6 rounded-[2px] flex items-center justify-center bg-nb-red text-white">
                        <IconNB name="close" size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </section>

            <section>
              <h2 className="text-lg font-semibold text-black mb-4 flex items-center gap-2">
                <span className="w-8 h-8 rounded-[2px] flex items-center justify-center text-sm font-bold bg-nb-yellow text-black border-2 border-black">5</span>
                Dates
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <DatePickerNB
                  label="Collection date"
                  value={formData.collectionDate}
                  onChange={(date) => setFormData(prev => ({ ...prev, collectionDate: date }))}
                  max={formData.estimatedDeliveryDate || undefined}
                />
                <DatePickerNB
                  label="Estimated delivery date"
                  value={formData.estimatedDeliveryDate}
                  onChange={(date) => { setFormData(prev => ({ ...prev, estimatedDeliveryDate: date })); clearFieldError('estimatedDeliveryDate'); }}
                  min={formData.collectionDate || undefined}
                  error={fieldErrors.estimatedDeliveryDate}
                />
              </div>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-black mb-4 flex items-center gap-2">
                <span className="w-8 h-8 rounded-[2px] flex items-center justify-center text-sm font-bold bg-nb-yellow text-black border-2 border-black">6</span>
                Additional information
              </h2>
              <div className="space-y-4">
                {/* Row 1: Part requirement + Estimated Price */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2 text-nb-text-secondary">Requires part?</label>
                    <div className="flex gap-3">
                      <label className="flex items-center gap-2 cursor-pointer group">
                        <input type="radio" name="requiresPart" value="yes"
                          checked={formData.requiresPart === 'yes'}
                          onChange={() => setFormData(prev => ({ ...prev, requiresPart: 'yes' }))}
                          className="sr-only peer" />
                        <span className="w-5 h-5 border-2 border-nb-black flex items-center justify-center peer-checked:bg-nb-yellow transition-colors">
                          <span className="w-2.5 h-2.5 bg-nb-black hidden peer-checked:block" />
                        </span>
                        <span className="text-sm font-medium text-nb-black">Yes</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer group">
                        <input type="radio" name="requiresPart" value="no"
                          checked={formData.requiresPart === 'no'}
                          onChange={() => { setFormData(prev => ({ ...prev, requiresPart: 'no', requiresPartName: '' })); setSparePartData(null); }}
                          className="sr-only peer" />
                        <span className="w-5 h-5 border-2 border-nb-black flex items-center justify-center peer-checked:bg-nb-yellow transition-colors">
                          <span className="w-2.5 h-2.5 bg-nb-black hidden peer-checked:block" />
                        </span>
                        <span className="text-sm font-medium text-nb-black">No</span>
                      </label>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1 text-nb-text-secondary">Estimated price</label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-nb-text-muted font-medium">$</span>
                    <input type="number" step="0.01" min="0" value={formData.estimatedPrice}
                      onChange={e => { setFormData(prev => ({ ...prev, estimatedPrice: e.target.value })); clearFieldError('estimatedPrice'); }}
                      className={`w-full pl-7 pr-4 py-2.5 bg-white border-2 ${fieldErrors.estimatedPrice ? 'border-nb-red' : 'border-black'} text-black focus:outline-none focus:border-nb-yellow rounded-[2px]`}
                      placeholder="0.00" />
                    {fieldErrors.estimatedPrice && (
                      <span className="text-xs text-nb-red mt-1 block">{fieldErrors.estimatedPrice}</span>
                    )}
                    </div>
                  </div>
                </div>
                {/* Row 2: Add spare part button (conditional) + Priority */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {formData.requiresPart === 'yes' && (
                    <div>
                      <label className="block text-sm font-medium mb-1 text-nb-text-secondary">Spare part</label>
                      {sparePartData ? (
                        <div className="flex items-center gap-2">
                          <div className="flex-1 px-3 py-2 bg-nb-cream border-2 border-nb-black rounded-[2px] text-sm">
                            <span className="font-medium">{sparePartData.description}</span>
                            {sparePartData.trackingNumber && (
                              <span className="text-nb-text-muted ml-2">Track: {sparePartData.trackingNumber}</span>
                            )}
                          </div>
                          <ButtonNB
                            type="button"
                            variant="secondary"
                            size="sm"
                            onClick={() => setShowSparePartModal(true)}>
                            Edit
                          </ButtonNB>
                          <ButtonNB
                            type="button"
                            variant="secondary"
                            size="sm"
                            onClick={() => setSparePartData(null)}>
                            <IconNB name="close" size={14} />
                          </ButtonNB>
                        </div>
                      ) : (
                        <ButtonNB
                          type="button"
                          variant="secondary"
                          size="md"
                          onClick={() => setShowSparePartModal(true)}>
                          <IconNB name="plus" size={16} />
                          Add spare part
                        </ButtonNB>
                      )}
                    </div>
                  )}
                  {formData.requiresPart !== 'yes' && <div />}
                  <div>
                    <label className="block text-sm font-medium mb-2 text-nb-text-secondary">Priority</label>
                    <div className="flex gap-3">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input type="radio" name="priority" value="normal"
                          checked={formData.priority === 'normal'}
                          onChange={() => setFormData(prev => ({
                            ...prev,
                            priority: 'normal',
                            estimatedDeliveryDate: new Date(Date.now() + 3 * 86400000).toISOString().split('T')[0],
                          }))}
                          className="sr-only peer" />
                        <span className="w-5 h-5 border-2 border-nb-black flex items-center justify-center peer-checked:bg-nb-yellow transition-colors">
                          <span className="w-2.5 h-2.5 bg-nb-black hidden peer-checked:block" />
                        </span>
                        <span className="text-sm font-medium text-nb-black">Normal</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input type="radio" name="priority" value="urgent"
                          checked={formData.priority === 'urgent'}
                          onChange={() => setFormData(prev => ({
                            ...prev,
                            priority: 'urgent',
                            estimatedDeliveryDate: new Date(Date.now() + 86400000).toISOString().split('T')[0],
                          }))}
                          className="sr-only peer" />
                        <span className="w-5 h-5 border-2 border-nb-black flex items-center justify-center peer-checked:bg-nb-yellow transition-colors">
                          <span className="w-2.5 h-2.5 bg-nb-black hidden peer-checked:block" />
                        </span>
                        <span className="text-sm font-medium text-nb-black">Urgent</span>
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            </section>
          </div>

          <div className="px-6 md:px-8 py-4 flex gap-3 justify-end border-t-2 border-black">
            <ButtonNB
              type="button"
              variant="secondary"
              size="md"
              onClick={handleReset}>
              Cancel
            </ButtonNB>
            <ButtonNB
              type="submit"
              variant="primary"
              size="md"
              loading={isSubmitting}>
              Save
            </ButtonNB>
          </div>
        </form>
      </CardNB>

      {/* Spare part creation modal — deferred mode: saves data locally, inserted after order creation */}
      <CreateSparePartModal
        isOpen={showSparePartModal}
        onClose={() => setShowSparePartModal(false)}
        onSave={(data) => {
          setSparePartData(data);
          setFormData(prev => ({ ...prev, requiresPartName: data.description }));
        }}
        defaultDescription={sparePartData?.description}
      />
    </div>
  );
}
